import { useQuery } from '@apollo/client/react/index.js';
import { GlassCard, GlassCardContent, GlassCardHeader } from '@merritrac/shared/components';
import {
  CheckCircle,
  ClipboardList,
  Clock,
  LogIn,
  LogOut,
  ArrowRightLeft,
  ShieldX,
  Timer,
  Wrench,
} from 'lucide-react';

import { GET_SERVICE_ORDER_TIMELINE } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

interface TimelineEvent {
  id: string;
  timestamp: string;
  eventType: string;
  description: string;
  performedBy: string | null;
  unitLabel: string | null;
  durationMinutes: number | null;
  location: string | null;
  previousValue: string | null;
  newValue: string | null;
}

interface TimelineQueryData {
  serviceOrderTimeline: TimelineEvent[];
}

const EVENT_CONFIG: Record<string, { icon: typeof Clock; color: string; label: string }> = {
  job_created: { icon: ClipboardList, color: 'text-primary', label: 'Created' },
  clock_in: { icon: LogIn, color: 'text-success', label: 'Clocked in' },
  clock_out: { icon: LogOut, color: 'text-warning', label: 'Clocked out' },
  unit_work_started: { icon: Timer, color: 'text-info', label: 'Started' },
  unit_work_completed: { icon: Wrench, color: 'text-success', label: 'Completed' },
  unit_completed: { icon: CheckCircle, color: 'text-success', label: 'Completed' },
  access_denied: { icon: ShieldX, color: 'text-danger', label: 'Access denied' },
  unit_access_denied: { icon: ShieldX, color: 'text-danger', label: 'Access denied' },
  status_change: { icon: ArrowRightLeft, color: 'text-warning', label: 'Status' },
  reassigned: { icon: ArrowRightLeft, color: 'text-info', label: 'Reassigned' },
  job_completed: { icon: CheckCircle, color: 'text-success', label: 'Job completed' },
  created: { icon: ClipboardList, color: 'text-primary', label: 'Created' },
};

function formatTime(iso: string): string {
  try {
    const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T'));
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T'));
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function dateKey(iso: string): string {
  // Handle both "2025-03-25T15:35:46" and "2025-03-25 15:35:46"
  return iso.split(/[T ]/)[0] ?? iso;
}

function formatDuration(minutes: number): string {
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${String(Math.round(minutes))}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${String(h)}h ${String(m)}m` : `${String(h)}h`;
}

function groupByDate(events: TimelineEvent[]): Map<string, TimelineEvent[]> {
  const groups = new Map<string, TimelineEvent[]>();
  for (const event of events) {
    const key = dateKey(event.timestamp);
    const existing = groups.get(key) ?? [];
    existing.push(event);
    groups.set(key, existing);
  }
  return groups;
}

interface Props {
  serviceOrderAk: number;
}

export function ActivityTimeline({ serviceOrderAk }: Props): ReactElement {
  const { data, loading, error } = useQuery<TimelineQueryData>(GET_SERVICE_ORDER_TIMELINE, {
    variables: { serviceOrderAk, limit: 100 },
    skip: serviceOrderAk <= 0,
  });

  const events = data?.serviceOrderTimeline ?? [];
  const dateGroups = groupByDate(events);

  return (
    <GlassCard>
      <GlassCardHeader>
        <h2 className="text-sm font-semibold text-text">Activity</h2>
        {events.length > 0 && (
          <span className="text-xs text-text-tertiary">{String(events.length)} events</span>
        )}
      </GlassCardHeader>
      <GlassCardContent>
        {loading && (
          <div className="space-y-2 py-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="skeleton w-4 h-4 rounded-full shrink-0" />
                <div className="skeleton h-3 rounded flex-1" />
              </div>
            ))}
          </div>
        )}

        {!loading && error !== undefined && (
          <p className="text-xs text-danger text-center py-4">Failed to load activity</p>
        )}

        {!loading && error === undefined && events.length === 0 && (
          <p className="text-xs text-text-secondary text-center py-4">No activity recorded yet.</p>
        )}

        {!loading && events.length > 0 && (
          <div className="max-h-[360px] overflow-y-auto -mx-1 px-1">
            {Array.from(dateGroups.entries()).map(([dk, groupEvents]) => (
              <div key={dk} className="mb-3 last:mb-0">
                {/* Date header */}
                <div className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wider mb-1.5 sticky top-0 bg-bg-elevated/90 backdrop-blur-sm py-0.5 z-10">
                  {formatDate(dk)}
                </div>

                {/* Events */}
                <div className="relative pl-5 border-l border-glass-border ml-1.5">
                  {groupEvents.map((event) => {
                    const config = EVENT_CONFIG[event.eventType] ?? {
                      icon: Clock,
                      color: 'text-text-secondary',
                      label: event.eventType,
                    };
                    const Icon = config.icon;

                    return (
                      <div key={event.id} className="relative flex items-baseline gap-2 py-1 group">
                        {/* Icon dot on the line */}
                        <div
                          className={`absolute -left-[23px] top-[7px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-bg-elevated ring-1 ring-glass-border ${config.color}`}
                        >
                          <Icon size={8} />
                        </div>

                        {/* Time */}
                        <span className="text-[10px] text-text-tertiary tabular-nums shrink-0 w-[52px]">
                          {formatTime(event.timestamp)}
                        </span>

                        {/* Description */}
                        <span className="text-xs text-text truncate flex-1">
                          {event.unitLabel !== null && event.unitLabel !== ''
                            ? event.unitLabel
                            : event.description}
                        </span>

                        {/* Tech */}
                        {event.performedBy !== null && event.performedBy !== '' && (
                          <span className="text-[10px] text-text-tertiary shrink-0">
                            {event.performedBy}
                          </span>
                        )}

                        {/* Duration */}
                        {event.durationMinutes !== null && (
                          <span className="text-[10px] font-medium text-text-secondary tabular-nums shrink-0">
                            {formatDuration(event.durationMinutes)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}
